// use client: search/filter/pagination state and router.push handlers
'use client'

import '@/styles/page.css'
import './guides.css'

import type { Article, ArticleTag, GuideCategory, GuidePagination } from './shared/types'

import { useRef, useState } from 'react'
import { Card, Col, Row, Stack } from 'react-bootstrap'
import { isEmpty } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import alrajhi_logo from '@/assets/images/alrajhi_logo.svg'
import { CardCarousel } from '@/components/carousel/CardCarousel'
import { Maintenance } from '@/components/maintenance'
import PaginationComponent from '@/components/pagination/PaginationComponent'
import { useIsMobile } from '@/hooks/useIsMobile'
import { convertMonthEnglishFormat, formatTitleWithItalic, parseCmsHtml } from '@/utils'
import { cmsImageSrc } from '@/utils/cmsImageSrc'
import { ArticleCard } from './shared/ArticleCard'
import { QuickFilterSidebar } from './shared/QuickFilterSidebar'
import { useArticleFilters } from './shared/useArticleFilters'

/*
 * The old react-slick config carried a 4-card desktop branch, but this carousel
 * lives inside `.d-flex.d-lg-none` — it is only ever visible below 992px, where
 * slick's `breakpoint: 992` entry (1 card, no arrows) applied. The desktop
 * branch was unreachable, so it is not carried over: one card, dots, no arrows.
 */

function Guides({
  initialData,
  initialFeatured = null,
  initialArticles = null,
  initialPagination = null,
  initialTags = null
}: {
  initialData?: GuideCategory[] | null
  initialFeatured?: Article[] | null
  initialArticles?: Article[] | null
  initialPagination?: GuidePagination | null
  initialTags?: ArticleTag[] | null
}) {
  // Seed featured + latest articles from the build so they're in the static HTML;
  // the client effect still refines them for filtering/sorting/pagination.
  const {
    articleList,
    guidesList,
    pagination,
    setPagination,
    search,
    setSearch,
    // Sort radios are uncontrolled on both guide pages: they write the sort and
    // never read it back. The hook consumes it internally to drive the fetch.
    setSelectedSort,
    selectedTopics,
    setSelectedTopics,
    isData
  } = useArticleFilters({
    initial: {
      articles: initialArticles,
      featured: initialFeatured,
      pagination: initialPagination
    },
    // The server already seeded page 1 — don't refetch it on mount.
    skipInitialFetch: initialArticles != null
  })

  // Content comes from the build-time snapshot only. A Strapi publish rebuilds
  // and redeploys the site, so there is nothing to revalidate on mount.
  // See tests/no-client-cms-fetch.spec.ts.
  const guideCategory: GuideCategory[] = initialData ?? []
  const articleTags: ArticleTag[] = initialTags ?? []
  const [sortActive, setSortActive] = useState(false)
  const [topicActive, setTopicActive] = useState(false)

  const isMobile = useIsMobile({ maxWidth: '360px' })
  const isTabScreen = useIsMobile({ maxWidth: '576px' })

  const router = useRouter()
  const ref = useRef(null)

  const latestArticle = (item, index) => (
    <ArticleCard
      key={item.slug ?? `article-${index}`}
      item={item}
      isMobile={isMobile}
      isTabScreen={isTabScreen}
      categoryBadge="responsive"
      onClick={() => router.push(`/personal/guides/${item?.guide_category?.slug}/${item.slug}/`)}
    />
  )

  return isData ? (
    <div id="guides" className="container page">
      {/* Banner Section */}
      <div style={{ marginBottom: '80px' }}>
        <h2 className="page-title">Guides</h2>
        <p className="tagline">
          Explore trusted insights, tips, and resources to make smarter financial decisions.
        </p>

        <Row className="justify-content-between flex-nowrap d-none d-lg-flex">
          {guidesList[0] && (
            <Col
              style={{
                flex: guidesList.length <= 2 ? '1' : '0 0 60%'
              }}
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <Link
                className="guide-main-card"
                style={{
                  backgroundImage: `url(${
                    guidesList?.[0]?.image?.image || guidesList?.[0]?.image?.imageLink || ''
                  })`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: guidesList.length <= 2 ? '80vh' : '500px'
                }}
                href={`/personal/guides/${guidesList?.[0]?.guide_category?.slug}/${guidesList?.[0]?.slug}/`}
              >
                <div className="w-100 guide-main-card-content" style={{ textOverflow: 'ellipsis' }}>
                  <p className="dateTag">
                    {guidesList[0]?.date && convertMonthEnglishFormat(guidesList[0]?.date)}
                  </p>
                  <h4>{guidesList[0]?.title && guidesList[0]?.title}</h4>
                  <p>
                    {guidesList && guidesList[0]?.subtitle
                      ? guidesList.length > 2
                        ? guidesList?.[0]?.subtitle?.length < 140
                          ? guidesList?.[0]?.subtitle
                          : guidesList?.[0]?.subtitle?.slice(0, 140) + '...'
                        : guidesList?.[0]?.subtitle
                      : null}
                  </p>
                  <div>
                    {(guidesList?.[0]?.article_tags?.length ?? 0) > 0 && (
                      <Stack direction="horizontal" gap={3} className="flex-wrap">
                        {guidesList?.[0]?.article_tags?.map(item => {
                          return (
                            <div key={item.title} className="stack-tag">
                              {item.title}
                            </div>
                          )
                        })}
                      </Stack>
                    )}
                  </div>
                </div>
              </Link>
            </Col>
          )}
          {guidesList.length > 2 && (
            <Col
              style={{
                flex: guidesList.length <= 2 ? '0' : '0 0 40%'
              }}
              className="d-flex justify-content-between flex-column"
            >
              {guidesList.slice(1, 4).map((element, elementIndex) => (
                <Row
                  key={element?.slug ?? `guide-row-${elementIndex}`}
                  data-aos="fade-left"
                  data-aos-delay="150"
                  onClick={() =>
                    router.push(
                      `/personal/guides/${element?.guide_category?.slug}/${element?.slug}/`
                    )
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex flex-column justify-content-between">
                    <div className="d-flex gap-3 justify-content-between">
                      <div
                        className="guide-side-image"
                        style={{
                          width:
                            guidesList.length === 4
                              ? '40%'
                              : guidesList.length === 3
                                ? '63%'
                                : '50%',
                          maxHeight: guidesList.length === 3 ? '240px' : '150px',
                          overflow: 'hidden',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <img
                          alt={element?.image?.altText}
                          src={cmsImageSrc(element?.image?.image?.url || element?.image?.imageLink)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                      <div
                        className="guide-side-content"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width:
                            guidesList.length === 4
                              ? '55%'
                              : guidesList.length === 3
                                ? '32%'
                                : '50%'
                        }}
                      >
                        {element?.title && (
                          <h6>
                            {element?.title?.length < 40
                              ? element?.title
                              : element?.title?.slice(0, 40) + '...'}
                          </h6>
                        )}
                        {element?.subtitle && (
                          <p>
                            {element?.subtitle?.length < 70
                              ? element?.subtitle
                              : element?.subtitle?.slice(0, 70) + '...'}
                          </p>
                        )}
                        <p>{convertMonthEnglishFormat(element.date)}</p>
                      </div>
                    </div>
                  </div>
                </Row>
              ))}
            </Col>
          )}
        </Row>
        <Row className="d-flex d-lg-none">
          <CardCarousel slidesPerView={1} slidesPerGroup={1} autoHeight showDots>
            {guidesList.slice(0, 4).map(guide => (
              <div className="guides-carousel-item" key={guide.id}>
                <Card
                  className="mx-sm-5 guide-carousel-card"
                  data-aos="fade-left"
                  data-aos-delay={`${Number(guide.id) + 1}00`}
                  onClick={() =>
                    router.push(`/personal/guides/${guide?.guide_category?.slug}/${guide.slug}/`)
                  }
                >
                  <Card.Img
                    style={{
                      borderRadius: '20px',
                      width: '100%',
                      height: '60%',
                      objectFit: guide.image ? `cover` : `scale-down`
                    }}
                    variant="top"
                    src={cmsImageSrc(guide?.image?.imageLink) || alrajhi_logo.src}
                  />
                  <Card.Body
                    style={{
                      height: '40%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div className="d-flex flex-row justify-content-between align-items-center">
                      <p className="fw-bold">
                        {guide?.date && convertMonthEnglishFormat(guide?.date)}
                      </p>
                    </div>
                    <h4 className="title">
                      {isMobile
                        ? guide?.title.slice(0, 35) + '...'
                        : guide?.title.length < 80
                          ? guide?.title
                          : guide?.title.slice(0, 80) + '...'}
                    </h4>
                    <p className="subtitle" style={{ textOverflow: 'ellipsis' }}>
                      {isMobile
                        ? parseCmsHtml(guide?.subtitle.slice(0, 100)) + '...'
                        : guide?.subtitle.length < 150
                          ? parseCmsHtml(guide?.subtitle)
                          : parseCmsHtml(guide?.subtitle.slice(0, 150)) + '...'}
                    </p>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </CardCarousel>
        </Row>
      </div>

      {/* Category Icons */}
      <div className="guide-category-container">
        {guideCategory &&
          guideCategory?.map((item, index) => (
            <Col
              className="d-flex flex-column align-items-center"
              data-aos="fade-up"
              data-aos-delay={`${index}00`}
              key={item.slug ?? `guide-category-${index}`}
              style={{
                width: '100px',
                flex: 'unset',
                color: 'var(--grey-500-body-text)',
                textAlign: 'center',
                cursor: 'pointer',
                margin: '0 8px'
              }}
              onClick={() => item.slug && router.push(`/personal/guides/${item.slug}/`)}
            >
              <div className="guides-icon-container mb-4">
                <img
                  src={cmsImageSrc(item.icon)}
                  alt="Shield Icon"
                  className="d-block"
                  width="100%"
                />
              </div>
              <div>
                <h6>{formatTitleWithItalic(item.title)}</h6>
              </div>
            </Col>
          ))}
      </div>

      {/* Latest Articles */}
      <div data-aos="fade-up" data-aos-delay="150" ref={ref}>
        <h2 className="page-title">Latest Articles</h2>
        <Row className="my-5 d-flex">
          <Col
            lg={3}
            className="guide-quick-filter-container"
            data-aos="fade-right"
            data-aos-delay="150"
          >
            <QuickFilterSidebar
              articleTags={articleTags}
              search={search}
              setSearch={setSearch}
              setSelectedSort={setSelectedSort}
              selectedTopics={selectedTopics}
              setSelectedTopics={setSelectedTopics}
              sortActive={sortActive}
              setSortActive={setSortActive}
              topicActive={topicActive}
              setTopicActive={setTopicActive}
            />
          </Col>
          <Col
            lg={9}
            className="guide-latest-article-container"
            data-aos="fade-left"
            data-aos-delay="150"
          >
            {!isEmpty(articleList) ? (
              articleList.map((item, index) => latestArticle(item, index))
            ) : (
              <div className="mx-2">No Data Available</div>
            )}
          </Col>
        </Row>
      </div>

      {/* todo: update style */}
      {pagination.pageCount > 1 && (
        <PaginationComponent
          scrollTargetRef={ref}
          pagination={pagination}
          setPagination={setPagination}
        />
      )}
      <div className="guides-footer" style={{ marginTop: '80px' }}>
        <p
          className="fst-italic"
          style={{ fontSize: '12px', color: '#000045B2', textAlign: 'justify' }}
        >
          <span className="fw-bold">Disclaimer:</span> These articles are prepared by Al Rajhi
          Banking & Investment Corporation (Malaysia) Bhd (“alrajhi bank Malaysia”). These articles
          are intended for Malaysian residents only. These articles must not be distributed in any
          jurisdiction where their distribution is unlawful. All unauthorized reproduction or use of
          this article will be the responsibility of the user and may lead to legal proceedings. The
          contents of these articles are for general circulation and information purposes only and
          do not constitute investment research or advice or a recommendation to buy or sell
          investment/banking products. Some of the statements contained in these articles may be
          considered forward-looking statements, which provide current expectations or forecasts of
          future events. Such forward-looking statements are not guarantees of future performance or
          events and involve risks and uncertainties. Actual results may differ materially from
          those described in such forward-looking statements as a result of various factors. Such
          forward-looking statements are subject to change. alrajhi bank Malaysia does not undertake
          any obligation to update the forward-looking statements contained herein, or to update the
          reasons why actual results could differ from those projected in the forward-looking
          statements. Past performance contained in these articles is not a reliable indicator of
          future performance whilst any forecasts, projections and simulations contained herein
          should not be relied upon as an indication of future results. These articles are not
          intended to provide and should not be relied on for accounting, legal or tax advice.
          Before you make any investment decision, you may wish to consult an independent financial
          adviser. In the event that you choose not to seek advice from a financial adviser, you
          should carefully consider whether the investment/banking product is suitable for you. You
          are advised to obtain appropriate professional advice where necessary. The accuracy and/or
          completeness of any third-party information obtained from sources which alrajhi bank
          Malaysia believes to be reliable might not have been independently verified; hence, you
          must seek from several sources prior to making any investment decision. The contents of
          this article have not been reviewed by any regulatory authority in Malaysia. You are
          advised to exercise caution in relation to the investment/banking product and this
          article. If you are in doubt about any of the contents of this article, you should obtain
          independent professional advice. No part of these articles may be reproduced, stored in a
          retrieval system, or transmitted, on any form or by any means, electronic, mechanical,
          photocopying, recording or otherwise, without the prior written permission of alrajhi bank
          Malaysia.
        </p>
      </div>
    </div>
  ) : (
    <Maintenance isComingSoon={true} />
  )
}

export default Guides
